/**
 * Итоги верхней сводки МСК Газобетона: материалы, доставка, услуги.
 *
 * Своей арифметики по деньгам здесь нет — складываются уже посчитанные суммы
 * строк блоков КП (row.sum). Ничего не переносится между разделами: строка рейса
 * остаётся доставкой, строка услуги остаётся услугой.
 *
 * Файл вынесен из GazProposalSummary.js отдельно, чтобы деление сумм проверялось
 * исполнением, а не сравнением строк исходника: компонент тянет React и CSS и
 * загрузчиком node не разбирается.
 */

// Ключи строк, которые заводит разгрузка. Ровно те же, что у createServiceRow в
// buildGazCommercialProposalBlocks.js: работа манипулятора и автокран. Рейсы сюда
// не попадают — они уходят в доставку, и переносить между ними ничего нельзя.
// Список нужен только для подписи: сумма берётся готовой из тех же row.sum.
export const GAZ_UNLOAD_SERVICE_KEYS = Object.freeze([
  'secondary-manipulator-service',
  'secondary-crane-service',
]);

/**
 * @param {Array} blocks — блоки КП
 * @returns {{materialsTotal:number, deliveryTotal:number, servicesTotal:number,
 *            serviceRows:Array<{key:string,name:string,sum:number}>,
 *            servicesAreUnloadingOnly:boolean, grandTotal:number}}
 *   `serviceRows` и `servicesAreUnloadingOnly` нужны только подписи: строка
 *   должна называть то, что в ней действительно лежит, а не обобщать состав,
 *   которого мы не знаем.
 */
export const buildGazProposalTotals = (blocks) => {
  const list = Array.isArray(blocks) ? blocks : [];
  const unloadKeys = new Set(GAZ_UNLOAD_SERVICE_KEYS);
  let materialsTotal = 0;
  let deliveryTotal = 0;
  let servicesTotal = 0;
  const serviceRows = [];

  for (const block of list) {
    for (const row of (Array.isArray(block?.rows) ? block.rows : [])) {
      const sum = Number(row?.sum) || 0;
      if (row?.kind === 'material') {
        materialsTotal += sum;
      } else if (row?.unit === 'рейс') {
        deliveryTotal += sum;
      } else {
        servicesTotal += sum;
        serviceRows.push({
          key: String(row?.key || ''),
          name: String(row?.name || '').trim(),
          sum,
        });
      }
    }
  }

  return {
    materialsTotal,
    deliveryTotal,
    servicesTotal,
    serviceRows,
    // Пустой список услуг разгрузкой не называем: подписывать нечего.
    servicesAreUnloadingOnly: serviceRows.length > 0
      && serviceRows.every((row) => unloadKeys.has(row.key)),
    grandTotal: materialsTotal + deliveryTotal + servicesTotal,
  };
};

/**
 * Итог «К оплате» в сводке одним правилом на все пути.
 *
 * Наличные расчёт уже округлил до 500 ₽ — показываются целым числом; безналичные
 * остаются с копейками. Правило одно и для нетронутого расчёта, и для ручных цен:
 * раньше ручной путь шёл через Math.ceil, и одна и та же сумма выглядела по-разному
 * до и после правки — 128 450,40 ₽ становились 128 450 ₽ и возвращались обратно
 * по кнопке «Сбросить цены».
 *
 * @param {number}  value        — сумма к оплате
 * @param {boolean} cashApplies  — применено ли округление наличных
 * @returns {string} готовая подпись с рублём
 */
export const formatGazPayable = (value, cashApplies) => {
  const number = Number(value) || 0;
  return cashApplies
    ? `${Math.round(number).toLocaleString('ru-RU')} ₽`
    : `${(Math.round(number * 100) / 100).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`;
};

export default buildGazProposalTotals;
